import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { BasketAPI } from '../api/basketApi';

interface CartItem {
  id: number;
  partUniqueId: string;
  count: number;
  price: number;
  // Добавьте другие поля по необходимости
}

interface CartContextType {
  items: CartItem[];
  totalCount: number;
  totalAmount: number;
  isLoading: boolean;
  refreshCart: () => Promise<void>;
  addItem: (partUniqueId: string, price: any) => Promise<void>;
  updateItem: (itemId: number, count: number) => Promise<void>;
  removeItem: (itemId: number) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const totalCount = items.reduce((sum, item) => sum + item.count, 0);
  const totalAmount = items.reduce((sum, item) => sum + (item.price * item.count), 0);

  const refreshCart = async () => {
    setIsLoading(true);
    try {
      const cartData = await BasketAPI.getItems();
      setItems(cartData || []);
    } catch (error) {
      console.error('Ошибка загрузки корзины:', error);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = async (partUniqueId: string, price: any) => {
    try {
      await BasketAPI.addItem(partUniqueId, price);
      await refreshCart(); // Обновляем корзину после добавления
    } catch (error) {
      console.error('Ошибка добавления товара в корзину:', error);
      throw error;
    }
  };

  const updateItem = async (itemId: number, count: number) => {
    try {
      await BasketAPI.updateItem(itemId, count);
      await refreshCart(); // Обновляем корзину после изменения
    } catch (error) {
      console.error('Ошибка изменения товара в корзине:', error);
      throw error;
    }
  };

  const removeItem = async (itemId: number) => {
    try {
      await BasketAPI.deleteItem(itemId);
      await refreshCart(); // Обновляем корзину после удаления
    } catch (error) {
      console.error('Ошибка удаления товара из корзины:', error);
      throw error;
    }
  };

  // Загружаем корзину при инициализации
  useEffect(() => {
    refreshCart();
  }, []);

  const value: CartContextType = {
    items,
    totalCount,
    totalAmount,
    isLoading,
    refreshCart,
    addItem,
    updateItem,
    removeItem,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
